/**
 * WM v1 Status Transition Engine
 * 
 * Statuses use DB format: checked-in, checked-out, unbedded, not_present, reserved, waitlist
 * Role restrictions: staff can only set checked-in, checked-out, not_present
 */

export type ClientStatus = 'checked-in' | 'checked-out' | 'unbedded' | 'not_present' | 'reserved' | 'waitlist' | 'hospital';

// Normalize any format to DB-compatible hyphenated format
export const normalizeStatus = (status: string): ClientStatus => {
  const map: Record<string, ClientStatus> = {
    'checked_in': 'checked-in',
    'checked_out': 'checked-out',
    'checked-in': 'checked-in',
    'checked-out': 'checked-out',
    'unbedded': 'unbedded',
    'not_present': 'not_present',
    'reserved': 'reserved',
    'waitlist': 'waitlist',
    'hospital': 'hospital',
  };
  return (map[status] || status) as ClientStatus;
};

export const displayStatus = (status: ClientStatus): string => {
  const map: Record<ClientStatus, string> = {
    'checked-in': 'Checked In',
    'checked-out': 'Checked Out',
    'unbedded': 'Unbedded',
    'not_present': 'Not Present',
    'reserved': 'Reserved',
    'waitlist': 'Wait List',
    'hospital': 'Hospital',
  };
  return map[status] || status;
};

/**
 * Universal status color system — single source of truth.
 * Includes display-only keys 'pass' and 'hospital' for badges/dots.
 *
 * Palette:
 *   checked-in   = green   (--status-checked-in)
 *   checked-out  = yellow  (--status-checked-out)
 *   pass         = purple  (--status-pass)
 *   unbedded     = gray    (--status-unbedded)
 *   not_present  = orange  (--status-not-present)
 *   hospital     = red     (--status-hospital)
 *   reserved     = blue    (--status-reserved)
 *   waitlist     = amber   (--status-waitlist)
 */
export type StatusKey = ClientStatus | 'pass';

// Static class maps so Tailwind JIT can detect the arbitrary-value classes.
const STATUS_SOLID: Record<StatusKey, string> = {
  'checked-in':  'bg-[hsl(var(--status-checked-in))] text-[hsl(var(--status-checked-in-foreground))]',
  'checked-out': 'bg-[hsl(var(--status-checked-out))] text-[hsl(var(--status-checked-out-foreground))]',
  'pass':        'bg-[hsl(var(--status-pass))] text-[hsl(var(--status-pass-foreground))]',
  'unbedded':    'bg-[hsl(var(--status-unbedded))] text-[hsl(var(--status-unbedded-foreground))]',
  'not_present': 'bg-[hsl(var(--status-not-present))] text-[hsl(var(--status-not-present-foreground))]',
  'hospital':    'bg-[hsl(var(--status-hospital))] text-[hsl(var(--status-hospital-foreground))]',
  'reserved':    'bg-[hsl(var(--status-reserved))] text-[hsl(var(--status-reserved-foreground))]',
  'waitlist':    'bg-[hsl(var(--status-waitlist))] text-[hsl(var(--status-waitlist-foreground))]',
};

const STATUS_SOFT: Record<StatusKey, string> = {
  'checked-in':  'bg-[hsl(var(--status-checked-in)/0.15)] text-[hsl(var(--status-checked-in))] border-[hsl(var(--status-checked-in)/0.3)]',
  'checked-out': 'bg-[hsl(var(--status-checked-out)/0.15)] text-[hsl(var(--status-checked-out))] border-[hsl(var(--status-checked-out)/0.3)]',
  'pass':        'bg-[hsl(var(--status-pass)/0.15)] text-[hsl(var(--status-pass))] border-[hsl(var(--status-pass)/0.3)]',
  'unbedded':    'bg-[hsl(var(--status-unbedded)/0.15)] text-[hsl(var(--status-unbedded))] border-[hsl(var(--status-unbedded)/0.3)]',
  'not_present': 'bg-[hsl(var(--status-not-present)/0.15)] text-[hsl(var(--status-not-present))] border-[hsl(var(--status-not-present)/0.3)]',
  'hospital':    'bg-[hsl(var(--status-hospital)/0.15)] text-[hsl(var(--status-hospital))] border-[hsl(var(--status-hospital)/0.3)]',
  'reserved':    'bg-[hsl(var(--status-reserved)/0.15)] text-[hsl(var(--status-reserved))] border-[hsl(var(--status-reserved)/0.3)]',
  'waitlist':    'bg-[hsl(var(--status-waitlist)/0.15)] text-[hsl(var(--status-waitlist))] border-[hsl(var(--status-waitlist)/0.3)]',
};

/** Returns design-system color classes for a given status. Default: soft (subtle). */
export const getStatusColor = (
  status: StatusKey,
  variant: 'solid' | 'soft' = 'soft'
): string => {
  const key = (STATUS_SOLID[status] ? status : 'checked-in') as StatusKey;
  return variant === 'solid' ? STATUS_SOLID[key] : STATUS_SOFT[key];
};

/** Resolve the status key for a client, accounting for active passes. */
export const resolveStatusKey = (client: {
  status?: string;
  passStart?: string | null;
  passEnd?: string | null;
  pass_start?: string | null;
  pass_end?: string | null;
}): StatusKey => {
  const start = client.passStart ?? client.pass_start ?? null;
  const end = client.passEnd ?? client.pass_end ?? null;
  if (start && end) {
    const now = new Date();
    if (now >= new Date(start) && now <= new Date(end)) return 'pass';
  }
  return normalizeStatus(client.status || 'checked-out') as StatusKey;
};

/** Human-readable label for any status key (incl. pass + hospital). */
export const getStatusLabel = (status: StatusKey): string => {
  if (status === 'pass') return 'Pass';
  return displayStatus(status as ClientStatus);
};

// All valid transitions as adjacency list
const TRANSITIONS: Record<ClientStatus, ClientStatus[]> = {
  'checked-out': ['checked-in', 'not_present', 'reserved', 'waitlist', 'hospital'],
  'checked-in': ['checked-out', 'unbedded', 'hospital'],
  'not_present': ['checked-in', 'checked-out', 'unbedded', 'hospital'],
  'reserved': ['checked-in', 'checked-out', 'waitlist'],
  'waitlist': ['reserved', 'checked-in'],
  'unbedded': ['waitlist', 'reserved', 'checked-in'],
  'hospital': ['checked-in', 'checked-out', 'unbedded'],
};

// Staff-allowed target statuses
const STAFF_ALLOWED_TARGETS: ClientStatus[] = ['checked-in', 'checked-out', 'not_present', 'hospital'];

export const isValidTransition = (from: ClientStatus, to: ClientStatus): boolean => {
  if (from === to) return false;
  const allowed = TRANSITIONS[from];
  return allowed ? allowed.includes(to) : false;
};

export const canRoleSetStatus = (role: 'staff' | 'admin', target: ClientStatus): boolean => {
  if (role === 'admin') return true;
  return STAFF_ALLOWED_TARGETS.includes(target);
};

export const getAvailableTransitions = (
  currentStatus: ClientStatus,
  role: 'staff' | 'admin'
): ClientStatus[] => {
  const allAllowed = TRANSITIONS[currentStatus] || [];
  if (role === 'admin') return allAllowed;
  return allAllowed.filter(s => STAFF_ALLOWED_TARGETS.includes(s));
};

export const getTransitionError = (from: ClientStatus, to: ClientStatus, role: 'staff' | 'admin'): string | null => {
  if (from === to) {
    return `Client is already ${displayStatus(to)}`;
  }
  if (!isValidTransition(from, to)) {
    return `Cannot transition from ${displayStatus(from)} to ${displayStatus(to)}`;
  }
  if (!canRoleSetStatus(role, to)) {
    return `Only administrators can set status to ${displayStatus(to)}`;
  }
  return null;
};

/** Returns true if the target status requires a room assignment */
export const requiresRoom = (target: ClientStatus): boolean => {
  return target === 'checked-in';
};

/** Returns true if the target status requires property_storage_notes or status_reason */
export const requiresAdditionalInfo = (target: ClientStatus): boolean => {
  return target === 'unbedded';
};

/** Check if a client is currently on an active pass */
export const isClientOnPass = (client: { passStart?: string; passEnd?: string }): boolean => {
  if (!client.passStart || !client.passEnd) return false;
  const now = new Date();
  return now >= new Date(client.passStart) && now <= new Date(client.passEnd);
};

/** Guard: can this client be unbedded? Returns error message or null */
export const getUnbedGuardError = (passEnd?: string | null): string | null => {
  if (!passEnd) return null;
  const end = new Date(passEnd);
  if (end > new Date()) {
    const formatted = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    return `Cannot unbed: client has an active pass until ${formatted}`;
  }
  return null;
};

/** Build the timestamp updates for a status change */
export const getTimestampUpdates = (newStatus: ClientStatus): Record<string, string | null> => {
  const now = new Date().toISOString();
  const updates: Record<string, string | null> = {};

  switch (newStatus) {
    case 'checked-in':
      updates.check_in_time = now;
      updates.check_out_time = null;
      // Returning from pass — clear any lingering pass window
      updates.pass_start = null;
      updates.pass_end = null;
      updates.pass_type = null;
      break;
    case 'checked-out':
      updates.check_out_time = now;
      break;
    case 'not_present':
      updates.not_present_at = now;
      break;
    case 'unbedded':
      updates.unbedded_at = now;
      break;
    case 'reserved':
      updates.reserved_at = now;
      break;
    case 'waitlist':
      updates.waitlist_at = now;
      break;
    case 'hospital':
      updates.hospital_at = now;
      break;
  }

  return updates;
};
